import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Button, Heading, Modal, Paragraph } from '@zillow/constellation-10';
import { UserVerificationFailedReason } from '@zg-rentals/ts-leasing-client';
import type { DatarouterError } from '@zg-rentals/rental-manager-env';
import type { VerifyUserInfoWebApiResponseDto } from '@zg-rentals/ts-leasing-client';
import { useQueryClient } from '@zg-rentals/react-query5';

import { useInterval } from 'react-use';
import { Lease, Person } from '../models';
import HttpStatusCode from '../models/HttpStatusCode';
import { useUserInfoTokenQuery, useVerifyUserInfoForOuidQuery, useVerifyUserInfoQuery } from '../utils/queries';
import { logError } from '@zg-rentals/log-error';

const ONE_SECOND = 1000;
const VERIFICATION_CALL_INTERVAL = 60 * ONE_SECOND;

type ModalProps = {
  isOpen: boolean;
  onConfirm: () => void;
};

function NoAccessModal({ onConfirm, isOpen }: ModalProps) {
  return (
    <Modal.Root
      size={{ base: 'fullScreen', md: 'sm' }}
      open={isOpen}
      onOpenChange={() => {}}
      useDismissProps={{ escapeKey: false, outsidePress: false }}
    >
      <Modal.Portal>
        <Modal.Backdrop>
          <Modal.Content>
            <Modal.Header>
              <Heading level={1}>No lease access</Heading>
            </Modal.Header>
            <Modal.Body>
              <Paragraph>You do not have access to this lease.</Paragraph>
            </Modal.Body>
            <Modal.Footer>
              <Button tone="brand" emphasis="filled" onClick={onConfirm}>
                Go to dashboard
              </Button>
            </Modal.Footer>
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal.Root>
  );
}

function SessionTimeoutModal({ onConfirm, isOpen }: ModalProps) {
  return (
    <Modal.Root
      size={{ base: 'fullScreen', md: 'sm' }}
      open={isOpen}
      onOpenChange={() => {}}
      useDismissProps={{ escapeKey: false, outsidePress: false }}
    >
      <Modal.Portal>
        <Modal.Backdrop>
          <Modal.Content>
            <Modal.Header>
              <Heading level={1}>Session timeout</Heading>
            </Modal.Header>
            <Modal.Body>
              <Paragraph>Your session has timed out.</Paragraph>
            </Modal.Body>
            <Modal.Footer>
              <Button tone="brand" emphasis="filled" onClick={onConfirm}>
                Reload page to login
              </Button>
            </Modal.Footer>
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal.Root>
  );
}

function TokenStolenModal({ onConfirm, isOpen }: ModalProps) {
  return (
    <Modal.Root
      size={{ base: 'fullScreen', md: 'sm' }}
      open={isOpen}
      onOpenChange={() => {}}
      useDismissProps={{ escapeKey: false, outsidePress: false }}
    >
      <Modal.Portal>
        <Modal.Backdrop>
          <Modal.Content>
            <Modal.Header>
              <Heading level={1}>Another tenant has used same account</Heading>
            </Modal.Header>
            <Modal.Body>
              <Paragraph>
                Your co-tenant has used the same Zillow account. You should use unique account to sign this lease.
              </Paragraph>
            </Modal.Body>
            <Modal.Footer>
              <Button tone="brand" emphasis="filled" onClick={onConfirm}>
                Go to dashboard
              </Button>
            </Modal.Footer>
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal.Root>
  );
}

type Props = {
  leaseId: number;
  isColandlord?: boolean;
};

function UserInfoVerifier({ leaseId, isColandlord }: Props) {
  const history = useHistory();
  const queryClient = useQueryClient();
  const [intervalDelay, setIntervalDelay] = useState<number | null>(null);

  const [showNoAccessModal, setShowNoAccessModal] = useState(false);
  const [showSessionTimeoutModal, setShowSessionTimeoutModal] = useState(false);
  const [showTokenStolenModal, setShowTokenStolenModal] = useState(false);

  const userInfoTokenQuery = useUserInfoTokenQuery(leaseId, { enabled: !isColandlord });
  const userInfoToken = userInfoTokenQuery.data;
  const ouid = isColandlord ? Person.getOUIDfromSearch() : '';

  const verifyUserInfoQuery = useVerifyUserInfoQuery(userInfoToken, false);
  const verifyUserInfoForOuidQuery = useVerifyUserInfoForOuidQuery(ouid, userInfoToken, false);

  function onDashboardRedirect() {
    history.push(Lease.getDashboardPath());
  }

  function onRefreshPage() {
    location.reload();
  }

  function handleFailure(reason: UserVerificationFailedReason) {
    switch (reason) {
      case UserVerificationFailedReason.NO_LEASE_ACCESS:
        // Lease can’t be accessed anymore, tell user what happened and redirect to dahsboard
        setShowNoAccessModal(true);
        setIntervalDelay(null);
        return;
      case UserVerificationFailedReason.LEASE_STATUS_MISMATCH:
        // Lease status is not shared anymore, refresh the view
        queryClient.invalidateQueries({
          predicate: (query) => query.meta?.tags?.includes('leasing:lease-status-change'),
        });
        return;
      case UserVerificationFailedReason.DOCUMENT_FIELDS_UPDATED:
        // Someone signed the document, refresh the view
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.meta?.tags?.includes('leasing:lease-status-change') ||
            query.meta?.tags?.includes('leasing:lease-preview'),
        });
        return;
      case UserVerificationFailedReason.USER_INDEX_DONT_MATCH:
        // Other tenant stole this user, explain and redirect to dashboard
        setShowTokenStolenModal(true);
        setIntervalDelay(null);
        return;
      case UserVerificationFailedReason.SESSION_NOT_VALID:
        // Session timeout, refresh the page for login
        setShowSessionTimeoutModal(true);
        setIntervalDelay(null);
        return;
      case UserVerificationFailedReason.BAD_TOKEN:
        // Ignore, it's fine
        return;
      default:
        // Ignore anything else
        return;
    }
  }

  async function verifyUser() {
    try {
      await (isColandlord
        ? verifyUserInfoForOuidQuery.refetch({ throwOnError: true })
        : verifyUserInfoQuery.refetch({ throwOnError: true }));
    } catch (error) {
      const datarouterError = error as DatarouterError;
      let reason: UserVerificationFailedReason;

      if (datarouterError.responseStatus === HttpStatusCode.FORBIDDEN) {
        reason = UserVerificationFailedReason.SESSION_NOT_VALID;
      } else {
        const errorResponse = datarouterError.errorDetails as VerifyUserInfoWebApiResponseDto;
        reason = errorResponse?.reason;
      }

      logError({
        error,
        context: { details: 'UserInfoVerifier - verifyUser', leaseId, reason },
      });

      handleFailure(reason);
    }
  }

  useEffect(() => setIntervalDelay(userInfoToken ? VERIFICATION_CALL_INTERVAL : null), [userInfoToken]);

  useInterval(verifyUser, intervalDelay);

  return (
    <>
      <NoAccessModal isOpen={showNoAccessModal} onConfirm={onDashboardRedirect} />
      <SessionTimeoutModal isOpen={showSessionTimeoutModal} onConfirm={onRefreshPage} />
      <TokenStolenModal isOpen={showTokenStolenModal} onConfirm={onDashboardRedirect} />
    </>
  );
}

export default UserInfoVerifier;
